import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, CheckCheck, AlertTriangle, ClipboardList, CalendarDays, Baby, Loader2, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { aiApi } from "./aiApi";
import AIMarkdown from "./AIMarkdown";
import AIInboxReply, { INBOX_REPLY_ROLES } from "./AIInboxReply";

const KIND = {
  injury: { label: "Kecederaan", icon: AlertTriangle, cls: "bg-rose-100 text-rose-600" },
  task: { label: "Tugasan", icon: ClipboardList, cls: "bg-violet-100 text-violet-600" },
  task_done: { label: "Tugasan selesai", icon: CheckCheck, cls: "bg-emerald-100 text-emerald-600" },
  parent_daily: { label: "Harian anak", icon: Baby, cls: "bg-pink-100 text-pink-600" },
  monthly_bmi: { label: "BMI bulanan", icon: CalendarDays, cls: "bg-sky-100 text-sky-600" },
  daily: { label: "Harian", icon: BellRing, cls: "bg-amber-100 text-amber-600" },
  daily_report: { label: "Laporan harian", icon: ClipboardList, cls: "bg-fuchsia-100 text-fuchsia-600" },
  weekly: { label: "Mingguan", icon: CalendarDays, cls: "bg-teal-100 text-teal-600" },
  weekly_report: { label: "Laporan mingguan", icon: CalendarDays, cls: "bg-teal-100 text-teal-600" },
  monthly: { label: "Bulanan", icon: CalendarDays, cls: "bg-indigo-100 text-indigo-600" },
  monthly_report: { label: "Laporan bulanan", icon: CalendarDays, cls: "bg-indigo-100 text-indigo-600" },
  reminder: { label: "Peringatan", icon: BellRing, cls: "bg-amber-100 text-amber-600" },
};
const fmt = (iso) => { try { return new Date(iso).toLocaleString("ms-MY", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

export default function AIInbox({ onRead, user }) {
  const canReply = !!user && INBOX_REPLY_ROLES.includes(user.role);
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => aiApi.inbox().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const readAll = async () => { await aiApi.markRead(); load(); onRead && onRead(); };
  const [ask, setAsk] = useState(null);
  const remove = async (n) => {
    try { await aiApi.deleteInbox(n.id); setItems((x) => x.filter((y) => y.id !== n.id)); onRead && onRead(); toast.success("Peringatan dipadam"); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal padam"); } finally { setAsk(null); }
  };
  const clear = async (scope) => {
    try { const r = await aiApi.clearInbox(scope); toast.success(r.message); load(); onRead && onRead(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal padam"); } finally { setAsk(null); }
  };
  const toggle = async (n) => {
    setOpen(open === n.id ? null : n.id);
    if (!n.is_read) {
      await aiApi.markRead([n.id]).catch(() => {});
      setItems((x) => x.map((y) => (y.id === n.id ? { ...y, is_read: true } : y)));
      onRead && onRead();
    }
  };

  if (items === null) return <div className="grid place-items-center h-40"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="h-full overflow-y-auto p-3 space-y-2" data-testid="ai-inbox">
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold text-slate-500">{items.filter((i) => !i.is_read).length} belum dibaca</div>
        <Button size="sm" variant="ghost" className="h-7 text-xs rounded-lg" onClick={readAll} data-testid="ai-inbox-read-all"><CheckCheck className="h-3.5 w-3.5 mr-1" /> Tanda semua dibaca</Button>
      </div>
      {items.length > 0 && (ask === "__all" || ask === "__read" ? (
        <div className="flex items-center gap-2 rounded-xl bg-rose-50 px-2.5 py-1.5" data-testid="ai-inbox-clear-confirm">
          <span className="flex-1 text-[11.5px] font-semibold text-rose-700">{ask === "__all" ? `Padam SEMUA ${items.length} peringatan?` : `Padam ${items.filter((i) => i.is_read).length} peringatan yang sudah dibaca?`}</span>
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setAsk(null)} data-testid="ai-inbox-clear-no">Tidak</Button>
          <Button size="sm" className="h-7 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs" onClick={() => clear(ask === "__all" ? "all" : "read")} data-testid="ai-inbox-clear-yes">Ya, padam</Button>
        </div>
      ) : (
        <div className="flex justify-end gap-1.5">
          <Button size="sm" variant="outline" className="h-7 rounded-lg text-xs text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => setAsk("__read")} data-testid="ai-inbox-clear-read"><Trash2 className="h-3.5 w-3.5 mr-1" />Padam yang dibaca</Button>
          <Button size="sm" variant="outline" className="h-7 rounded-lg text-xs text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => setAsk("__all")} data-testid="ai-inbox-clear-all"><Trash2 className="h-3.5 w-3.5 mr-1" />Padam semua</Button>
        </div>
      ))}
      {items.length === 0 && (
        <div className="text-center py-10 text-slate-400 text-sm"><BellRing className="h-8 w-8 mx-auto mb-2 opacity-50" />Tiada peringatan lagi. AI akan menghantar peringatan harian, mingguan & bulanan di sini.</div>
      )}
      {items.map((n) => {
        const base = KIND[n.kind] || { label: "Peringatan", icon: BellRing, cls: "bg-amber-100 text-amber-600" };
        const k = user?.role === "ibu_bapa" && n.kind === "task" ? { ...base, label: "Makluman Am" } : base;
        const Icon = k.icon;
        return (
          <div key={n.id} className={`rounded-2xl border bg-white transition-shadow hover:shadow-md ${n.is_read ? "border-slate-100" : "border-amber-200 shadow-sm"}`}>
            <div className="flex items-start">
            <button className="flex-1 min-w-0 flex items-start gap-2 p-3 text-left" onClick={() => toggle(n)} data-testid="ai-inbox-item">
              <span className={`h-8 w-8 shrink-0 rounded-xl grid place-items-center ${k.cls}`}><Icon className="h-4 w-4" /></span>
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-1">
                  <span className="font-bold text-[13px] text-slate-800 truncate">{n.title}</span>
                  {!n.is_read && <span className="h-2 w-2 rounded-full bg-rose-500 shrink-0" />}
                </span>
                <span className="block text-[11px] text-slate-400">{k.label} • {fmt(n.created_at)} • {n.sender?.name || "AI"}</span>
                {open !== n.id && <span className="block text-xs text-slate-500 line-clamp-2 mt-0.5">{(n.message || "").replace(/[#*_!\[\]()]/g, "").slice(0, 140)}</span>}
              </span>
            </button>
            <button onClick={() => setAsk(ask === n.id ? null : n.id)} className="h-9 w-9 mt-2 mr-1.5 shrink-0 grid place-items-center rounded-xl text-slate-300 hover:text-rose-600 hover:bg-rose-50 transition-colors" aria-label="Padam peringatan" data-testid="ai-inbox-delete">
              <Trash2 className="h-4 w-4" />
            </button>
            </div>
            {ask === n.id && (
              <div className="mx-3 mb-2 flex items-center gap-2 rounded-xl bg-rose-50 px-2.5 py-1.5" data-testid="ai-inbox-delete-confirm">
                <span className="flex-1 text-[11.5px] font-semibold text-rose-700">Padam peringatan ini?</span>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setAsk(null)} data-testid="ai-inbox-delete-no">Tidak</Button>
                <Button size="sm" className="h-7 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs" onClick={() => remove(n)} data-testid="ai-inbox-delete-yes">Ya, padam</Button>
              </div>
            )}
            {open === n.id && (
              <div className="px-3 pb-3 -mt-1">
                <AIMarkdown>{n.message}</AIMarkdown>
                {canReply && <AIInboxReply notification={n} user={user} />}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
